'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Cpu, BookOpen, Menu, X, Zap, LogOut, User, Download } from 'lucide-react';
import ClientIcon from '@/components/ui/ClientIcon';
import { useAuth } from '@/contexts/AuthContext';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const navItems = [
  { label: 'Architecture Canvas', href: '/', icon: Cpu },
  { label: 'Learning Dashboard', href: '/learning-dashboard', icon: BookOpen },
];

export default function TopNav() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const { user, signOut } = useAuth();

  useEffect(() => {
    const handleBeforeInstall = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', () => setInstallPrompt(null));
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const handleSignOut = async () => {
    setProfileOpen(false);
    try {
      await signOut();
      window.location.href = '/login';
    } catch {}
  };

  const userInitial = user?.user_metadata?.full_name?.[0]?.toUpperCase()
    || user?.email?.[0]?.toUpperCase()
    || 'U';

  const userName = user?.user_metadata?.full_name || '';
  const userEmail = user?.email || '';

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="h-14 border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-50 flex items-center px-3 sm:px-4 gap-3">
      {/* Logo */}
      <Link href="/" className="flex items-center gap-2 shrink-0">
        <AppLogo size={28} />
        <span className="font-semibold text-sm tracking-tight text-foreground hidden sm:block">
          ArchSim
        </span>
        <span className="skill-badge bg-primary/10 text-primary ml-1 hidden sm:inline-flex">
          Beta
        </span>
      </Link>

      {/* Desktop Nav items */}
      <nav className="hidden md:flex items-center gap-1 ml-2">
        {navItems?.map((item) => {
          const isActive = pathname === item?.href || (item?.href !== '/' && pathname?.startsWith(item?.href));
          return (
            <Link
              key={`nav-${item?.href}`}
              href={item?.href}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150 ${
                isActive
                  ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
            >
              <ClientIcon icon={item?.icon} size={14} />
              {item?.label}
            </Link>
          );
        })}
      </nav>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Simulation Ready badge — desktop only */}
      <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-positive/10 border border-positive/20 shrink-0">
        <ClientIcon icon={Zap} size={12} className="text-positive" />
        <span className="text-xs font-medium text-positive font-mono-nums">Simulation Ready</span>
      </div>

      {/* Install App — shown only when the browser signals the PWA is installable */}
      {installPrompt && (
        <button
          onClick={handleInstall}
          className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-all shrink-0"
        >
          <ClientIcon icon={Download} size={13} />
          Install App
        </button>
      )}

      {/* Profile dropdown — always visible on ALL screen sizes */}
      <div className="relative shrink-0" ref={profileRef}>
        <button
          onClick={() => {
            setProfileOpen(!profileOpen);
            setMobileOpen(false);
          }}
          className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-info flex items-center justify-center text-xs font-bold text-background hover:ring-2 hover:ring-primary/50 transition-all cursor-pointer shrink-0"
          aria-label="Profile menu"
        >
          {user ? (
            <span>{userInitial}</span>
          ) : (
            <ClientIcon icon={User} size={14} />
          )}
        </button>

        {profileOpen && (
          <div className="absolute right-0 top-10 w-56 bg-card border border-border rounded-lg shadow-xl z-[100] overflow-hidden">
            {/* User info */}
            <div className="px-4 py-3 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-info flex items-center justify-center text-sm font-bold text-background shrink-0">
                  {user ? userInitial : <ClientIcon icon={User} size={14} />}
                </div>
                <div className="min-w-0 flex-1">
                  {userName && (
                    <p className="text-sm font-medium text-foreground truncate">{userName}</p>
                  )}
                  {userEmail ? (
                    <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Not signed in</p>
                  )}
                </div>
              </div>
            </div>

            {/* Sign out */}
            {user ? (
              <button
                onClick={handleSignOut}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-destructive hover:bg-destructive/10 transition-all"
              >
                <ClientIcon icon={LogOut} size={15} />
                Sign out
              </button>
            ) : (
              <Link
                href="/login"
                onClick={() => setProfileOpen(false)}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-foreground hover:bg-secondary transition-all"
              >
                Sign in
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Mobile hamburger */}
      <button
        className="md:hidden w-9 h-9 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-all shrink-0"
        onClick={() => {
          setMobileOpen(!mobileOpen);
          setProfileOpen(false);
        }}
        aria-label="Toggle menu"
      >
        {mobileOpen ? <ClientIcon icon={X} size={18} /> : <ClientIcon icon={Menu} size={18} />}
      </button>

      {/* Mobile nav drawer */}
      {mobileOpen && (
        <div className="absolute top-14 left-0 right-0 bg-card border-b border-border p-4 flex flex-col gap-2 md:hidden z-50 max-h-[calc(100dvh-56px)] overflow-y-auto overscroll-contain">
          {navItems?.map((item) => {
            const isActive = pathname === item?.href;
            return (
              <Link
                key={`mobile-nav-${item?.href}`}
                href={item?.href}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
              >
                <ClientIcon icon={item?.icon} size={16} />
                {item?.label}
              </Link>
            );
          })}
          {installPrompt && (
            <button
              onClick={() => { handleInstall(); setMobileOpen(false); }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-all"
            >
              <ClientIcon icon={Download} size={16} />
              Install App
            </button>
          )}
        </div>
      )}
    </header>
  );
}